<template>
  <menu
    :class="$style.root"
  >
    <div :class="$style.content">
      <ul :class="$style.list">
        <li
          v-for="item in items"
          :key="item?.id"
          :class="$style.item"
        >
          <navigation-menu-item-component :item="item" />
        </li>
      </ul>

      <client-only>
        <ul
          :class="$style.list"
          v-if="entries?.data && appVariant === 'main'"
        >
          <li
            v-for="item in entries?.data"
            :key="item?.id"
          >
            <navigation-menu-item-secondary-component :item="item" />
          </li>
        </ul>
      </client-only>

      <shared-newsletter-component />
    </div>
  </menu>
</template>

<script setup>
const appVariant = getAppVariant()
const nuxtApp = useNuxtApp()
const { $i18n } = nuxtApp
const { locale } = $i18n

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

// 1
const { data, error } = await useAsyncData(() => {
  const slug = getAppVariant() === 'main' ? 'menu' : 'interim-menu'
  return $fetch('/api/strapi-rest', { query: { path: queryMenu(getPageQuery({slug: slug})) } } )
}, { watch: [locale] } )

watch(data, () => {
  if (data?.value?.documentId) return
  data.value = getFirstObject(data.value?.data)
}, { immediate: true })

const items = computed(() => {
  return data.value?.items?.filter((item) => {
    return getRoutePresence(getNavigationItem(item)?.link?.routeObject?.name)
  })
})

// 2
const { data: entries, error: entriesError, execute: executeEntries } = await useAsyncData(() => {
  return $fetch('/api/strapi-rest', { query: { path: queryBiennialEntriesThumbnailBasic(getPageQuery()) } } )
}, { immediate: false } )

if (appVariant === 'main') {
  executeEntries()
}

// 3
const emit = defineEmits(['closeSelf'])

onKeyStroke('Escape', (e) => {
  emit('closeSelf')
})

const timeout = ref(null)

watch(() => props?.isOpen, (val) => {
  if (val) {
    timeout.value = setTimeout(() => {
      document.documentElement.setAttribute('data-overflow-hidden', '')
    }, 100)
  } else {
    document.documentElement.removeAttribute('data-overflow-hidden', '')
  }
})

onBeforeUnmount(() => {
  document.documentElement.removeAttribute('data-overflow-hidden', '')

  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})
</script>

<style module lang="scss">
.root {
  composes: reset-focus from global;

  --color--background: var(--color--background--default);
  --color--primary: var(--color--primary--default);
  --color--secondary: var(--color--secondary--default);
  --color--text-decoration: var(--color--secondary--default);

  position: fixed;
  z-index: var(--z-index--menu);

  margin: 0;
  padding: 0;

  top: 0;
  left: 0;
  bottom: 0;
  left: 0;

  width: 100%;
  height: 100%;

  background-color: var(--color--background);
  color: var(--color--primary);

  display: flex;
  flex-direction: column;

  overflow: scroll;

  @media (max-width: $breakpoint-s) {
    justify-content: flex-start;
  }
}

.content {
  composes: container from global;
  padding-top: calc(var(--unit--vertical) * 2);
  padding-bottom: calc(var(--unit--vertical) * 2);

  display: flex;
  flex-direction: column;
  gap: calc(var(--unit--vertical) * 2);

  margin: auto;
}

.list {
  composes: reset-list from global;

  columns: 2;
  column-gap: calc(var(--unit--horizontal) * 2);

  @media (max-width: $breakpoint-s) {
    columns: 1;
  }
}
</style>
